import { useState, useEffect, useCallback, useRef } from 'react';
import './FilterDrawer.css';

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const EMPTY_FILTERS = {
  size: [],
  priceMin: '',
  priceMax: '',
  subCategory: [],
  isNew: false,
};

/* ── Count active filters for badge on trigger button ── */
export function countActiveFilters(filters) {
  if (!filters) return 0;
  let count = 0;
  if (filters.size?.length)         count += filters.size.length;
  if (filters.priceMin || filters.priceMax) count += 1;
  if (filters.subCategory?.length) count += 1;
  if (filters.isNew)               count += 1;
  return count;
}

function FilterDrawer({ isOpen, onClose, filters, onFiltersChange, subCategories = [] }) {
  const [localFilters, setLocalFilters] = useState({ ...EMPTY_FILTERS, ...filters });
  const [visible, setVisible] = useState(false);
  const drawerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setLocalFilters({ ...EMPTY_FILTERS, ...filters });
      setVisible(true);
    }
  }, [isOpen, filters]);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(onClose, 340);
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [handleClose]);

  const handleMultiToggle = useCallback((key, value) => {
    setLocalFilters((prev) => {
      const current = prev[key] || [];
      return {
        ...prev,
        [key]: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });
  }, []);

  const handleSubCategoryToggle = useCallback((value) => {
    setLocalFilters((prev) => {
      const current = prev.subCategory || [];
      return {
        ...prev,
        subCategory: current.includes(value) && current.length === 1 ? [] : [value],
      };
    });
  }, []);

  const handlePrice = useCallback((field, value) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleNewArrivalsToggle = useCallback(() => {
    setLocalFilters((prev) => ({ ...prev, isNew: !prev.isNew }));
  }, []);

  const handleClearAll = useCallback(() => {
    setLocalFilters(EMPTY_FILTERS);
    onFiltersChange(EMPTY_FILTERS);
  }, [onFiltersChange]);

  const handleApply = useCallback(() => {
    onFiltersChange(localFilters);
    handleClose();
  }, [localFilters, onFiltersChange, handleClose]);

  const activeCount = countActiveFilters(localFilters);

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`fd-overlay${visible ? ' fd-overlay--visible' : ''}`}
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        ref={drawerRef}
        className={`fd${visible ? ' fd--visible' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Product filters"
      >
        <div className="fd__head">
          <div className="fd__head-left">
            <h2 className="fd__title">Filters</h2>
            {activeCount > 0 && (
              <span className="fd__count" aria-label={`${activeCount} active filters`}>
                {activeCount}
              </span>
            )}
          </div>
          <button className="fd__close" onClick={handleClose} aria-label="Close filters">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="fd__body">

          {/* Size */}
          <section className="fd__section" aria-labelledby="filter-size">
            <h3 className="fd__section-title" id="filter-size">Size</h3>
            <div className="fd__size-grid" role="group" aria-label="Select sizes">
              {SIZES.map((size) => (
                <button
                  key={size}
                  className={`fd__size-btn${localFilters.size?.includes(size) ? ' fd__size-btn--on' : ''}`}
                  onClick={() => handleMultiToggle('size', size)}
                  aria-pressed={localFilters.size?.includes(size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </section>

          {/* Price */}
          <section className="fd__section" aria-labelledby="filter-price">
            <h3 className="fd__section-title" id="filter-price">Price Range</h3>
            <div className="fd__price-row">
              <div className="fd__price-field">
                <span className="fd__price-symbol">₹</span>
                <input
                  type="number"
                  placeholder="Min"
                  value={localFilters.priceMin}
                  onChange={(e) => handlePrice('priceMin', e.target.value)}
                  className="fd__price-input"
                  min="0"
                  aria-label="Minimum price"
                  style={{ fontSize: '16px' }}
                />
              </div>
              <span className="fd__price-sep" aria-hidden="true">–</span>
              <div className="fd__price-field">
                <span className="fd__price-symbol">₹</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={localFilters.priceMax}
                  onChange={(e) => handlePrice('priceMax', e.target.value)}
                  className="fd__price-input"
                  min="0"
                  aria-label="Maximum price"
                  style={{ fontSize: '16px' }}
                />
              </div>
            </div>
          </section>

          {/* Sub-category */}
          {subCategories.length > 0 && (
            <section className="fd__section" aria-labelledby="filter-category">
              <h3 className="fd__section-title" id="filter-category">Category</h3>
              <div className="fd__chip-list" role="group" aria-label="Select category">
                {subCategories.map((cat) => (
                  <button
                    key={cat}
                    className={`fd__chip${localFilters.subCategory?.includes(cat) ? ' fd__chip--on' : ''}`}
                    onClick={() => handleSubCategoryToggle(cat)}
                    aria-pressed={localFilters.subCategory?.includes(cat)}
                  >
                    {cat.charAt(0).toUpperCase() + cat.slice(1).replace('-', ' ')}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* New Arrivals */}
          <section className="fd__section fd__section--inline" aria-labelledby="filter-new">
            <h3 className="fd__section-title" id="filter-new">New Arrivals</h3>
            <button
              type="button"
              className={`fd__toggle${localFilters.isNew ? ' fd__toggle--on' : ''}`}
              onClick={handleNewArrivalsToggle}
              role="switch"
              aria-checked={localFilters.isNew}
              aria-label="Show new arrivals only"
            >
              <span className="fd__toggle-track" aria-hidden="true">
                <span className="fd__toggle-thumb" />
              </span>
              <span className="fd__toggle-label">New arrivals only</span>
            </button>
          </section>

        </div>

        <div className="fd__foot">
          {activeCount > 0 && (
            <button type="button" className="fd__clear" onClick={handleClearAll}>
              Clear all ({activeCount})
            </button>
          )}
          <button type="button" className="fd__apply" onClick={handleApply}>
            {activeCount > 0 ? 'Show Results' : 'Apply'}
          </button>
        </div>
      </div>
    </>
  );
}

export default FilterDrawer;
